<template>
  <v-dialog
    v-model="show"
    max-width="1200"
    scrollable
    content-class="ds-dialog-chat"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    transition="dialog-bottom-transition"
  >
    <v-card v-if="localNote" class="rt-card">
      <v-card-title class="rt-header">
        <v-icon color="white" small class="mr-2">mdi-message-reply-text-outline</v-icon>
        <span class="rt-parties">
          {{ localNote.sender_name }} →
          {{ localNote.recipient_name || localNote.clinic_name }}
        </span>
        <v-spacer />
        <v-chip
          x-small
          :color="localNote.resolved_at ? 'green' : 'amber darken-2'"
          text-color="white"
          class="mr-2"
        >
          {{ localNote.resolved_at ? "Resolvido" : "Pendente" }}
        </v-chip>
        <v-btn class="close-btn" icon color="white" @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="rt-body">
        <div
          class="rt-msg"
          :class="{ 'rt-msg--mine': isMine(localNote.sender_id) }"
        >
          <div class="rt-msg-author">{{ localNote.sender_name }}</div>
          <div class="rt-msg-text">{{ localNote.message }}</div>
          <div class="rt-msg-date">{{ formatDate(localNote.created_at) }}</div>
        </div>
        <div
          v-for="r in localNote.replies"
          :key="r.id"
          class="rt-msg"
          :class="{ 'rt-msg--mine': isMine(r.sender_id) }"
        >
          <div class="rt-msg-author">{{ r.sender_name }}</div>
          <div class="rt-msg-text">{{ r.message }}</div>
          <div class="rt-msg-date">{{ formatDate(r.created_at) }}</div>
        </div>
      </v-card-text>

      <!-- Só aparece com o botão: resolvido, viraria uma faixa vazia. -->
      <v-card-actions v-if="!localNote.resolved_at" class="rt-foot ds-dialog-actions">
        <v-btn
          text
          small
          color="green darken-1"
          :loading="resolving"
          @click="resolve"
        >
          <v-icon left small>mdi-check</v-icon>
          Marcar resolvido
        </v-btn>
        <v-spacer />
      </v-card-actions>

      <div class="rt-reply-box">
        <v-text-field
          v-model="replyText"
          dense
          outlined
          hide-details="auto"
          placeholder="Responder..."
          @keyup.enter="sendReply"
        />
        <v-btn
          icon
          color="primary"
          :loading="sending"
          :disabled="!replyText.trim()"
          @click="sendReply"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatDate } from "@/utils/datetime";
import {
  replyToAssistantNote,
  resolveAssistantNote,
} from "@/services/assistantNoteService";

export default {
  name: "RecadoThreadDialog",

  props: {
    value: { type: Boolean, default: false },
    note: { type: Object, default: null },
    currentUserId: { type: [Number, String], default: null },
  },

  data() {
    return { localNote: null, replyText: "", sending: false, resolving: false };
  },

  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
  },

  watch: {
    note: {
      immediate: true,
      handler(n) {
        this.localNote = n ? { ...n } : null;
        this.replyText = "";
      },
    },
  },

  methods: {
    formatDate,
    isMine(id) {
      return Number(id) === Number(this.currentUserId);
    },
    async sendReply() {
      const text = this.replyText.trim();
      if (!text || !this.localNote) return;
      this.sending = true;
      try {
        const { data } = await replyToAssistantNote(this.localNote.id, text);
        if (data && data.data) this.localNote = data.data;
        this.replyText = "";
        this.$emit("updated");
      } catch (e) {
        console.error("Erro ao responder recado:", e);
      } finally {
        this.sending = false;
      }
    },
    async resolve() {
      if (!this.localNote) return;
      this.resolving = true;
      try {
        const { data } = await resolveAssistantNote(this.localNote.id);
        if (data && data.data) this.localNote = data.data;
        this.$emit("updated");
      } catch (e) {
        console.error("Erro ao resolver recado:", e);
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.rt-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 16px;
}
.rt-parties {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}
/* salvia-848: sem max-height proprio — o flex do `scrollable` faz o corpo
   ocupar toda a sobra do card, que agora tem altura fixa (ds-dialog-chat). */
.rt-body {
  padding: 14px 16px;
}
.rt-msg {
  background: #f3f5f4;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 8px;
  max-width: 85%;
}
.rt-msg--mine {
  background: #e6efea;
  margin-left: auto;
}
.rt-msg-author {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--ds-text-muted, var(--ds-text-muted));
  margin-bottom: 2px;
}
.rt-msg-text {
  font-size: 13px;
  line-height: 1.4;
  color: var(--ds-text-strong, #1c1917);
  white-space: pre-wrap;
  word-break: break-word;
}
.rt-msg-date {
  font-size: 10px;
  color: var(--ds-text-muted, #9a958c);
  margin-top: 3px;
  text-align: right;
}
/* O rodapé (ds-dialog-actions) e a caixa de resposta formam um bloco só:
   mesmo fundo areia e o mesmo recuo lateral. */
.rt-reply-box {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 20px 14px;
  background-color: var(--ds-blue-50);
}
</style>
